// hooks/useChatbot.ts
import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabaseClient';
import { claudeServiceV2 } from '../services/claudeServiceV2';
import { conversationService } from '../services/conversationService';
import { notificationService } from '../services/notificationService';
import { followUpService } from '../services/followUpService';
import { inquiryService } from '../services/inquiryService';
import { pricingWithLeadService } from '../services/pricingWithLeadService';
import type { ChatMessage, ChatConversation, LeadFormData } from '../types/chat.types';

interface UseChatbotOptions {
  conversationSource?: string;
  welcomeMessage?: string;
}

export const useChatbot = (options: UseChatbotOptions = {}) => {
  const { conversationSource = 'app', welcomeMessage } = options;
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [conversation, setConversation] = useState<ChatConversation | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [awaitingLeadData, setAwaitingLeadData] = useState(false);

  // Załaduj historię konwersacji jeśli istnieje
  useEffect(() => {
    const loadConversation = async () => {
      const savedConversationId = localStorage.getItem('chatbot_conversation_id');
      if (savedConversationId) {
        try {
          const conv = await conversationService.getConversation(savedConversationId);
          if (conv && conv.status === 'active') {
            setConversation(conv);
            const msgs = await conversationService.getMessages(savedConversationId);
            setMessages(msgs);
          }
        } catch (error) {
          console.error('Error loading conversation:', error);
        }
      }
    };
    
    loadConversation();
  }, []);

  const startConversation = async (leadData?: LeadFormData) => {
    try {
      let leadId = null;
      let newConversation: ChatConversation;
      
      // Jeśli mamy dane leada, najpierw go utwórz
      if (leadData) {
        // Najpierw tworzymy konwersację bez leadId
        newConversation = await conversationService.createConversation({
          leadId: null,
          source: conversationSource
        });
        
        // Teraz twórz leada z ID konwersacji
        const { data: lead, error: leadError } = await supabase
          .from('leads')
          .insert({
            company_name: leadData.company,
            contact_name: leadData.name,
            email: leadData.email,
            phone: leadData.phone,
            source: 'chatbot',
            notes: leadData.message,
            status: 'new'
          })
          .select()
          .single();
          
        if (leadError) throw leadError;
        leadId = lead.id;
        
        // Aktualizuj konwersację z leadId
        await supabase
          .from('conversations')
          .update({ lead_id: leadId })
          .eq('id', newConversation.id);
        
        // Wyślij powiadomienie o nowym leadzie
        await notificationService.notifyNewLead(lead, newConversation.id);
        console.log('✅ Powiadomienie o nowym leadzie wysłane!');
        
      } else {
        // Konwersacja bez leada
        newConversation = await conversationService.createConversation({
          leadId: null,
          source: conversationSource
        });
      }
      
      setConversation(newConversation);
      // Upewnij się, że zapisujemy string do localStorage
      if (newConversation && newConversation.id) {
        localStorage.setItem('chatbot_conversation_id', String(newConversation.id));
      }

      // Wyślij wiadomość powitalną
      const welcomeMessage: ChatMessage = {
        id: crypto.randomUUID(),
        conversationId: newConversation.id,
        role: 'assistant',
        content: leadData 
          ? `Dziękuję ${leadData.name}! Miło mi Cię poznać. W czym mogę pomóc?`
          : 'Witaj! 👋 Jestem asystentem PlexiSystem. W czym mogę Ci pomóc?',
        timestamp: new Date()
      };
      
      setMessages([welcomeMessage]);
      await conversationService.saveMessage(welcomeMessage);
      
    } catch (error) {
      setError('Nie udało się rozpocząć konwersacji');
      console.error('Error starting conversation:', error);
    }
  };

  const sendMessage = async (content: string) => {
    if (!conversation) {
      await startConversation();
      return;
    }

    setIsLoading(true);
    setError(null);

    // Dodaj wiadomość użytkownika
    const userMessage: ChatMessage = {
      id: crypto.randomUUID(),
      conversationId: conversation.id,
      role: 'user',
      content,
      timestamp: new Date()
    };
    
    setMessages(prev => [...prev, userMessage]);
    await conversationService.saveMessage(userMessage);

    try {
      // NOWA LOGIKA: Sprawdź czy wiadomość wymaga wyceny
      const pricingResult = await pricingWithLeadService.processMessage(
        conversation.id,
        content
      );
      
      // Jeśli wykryto zapytanie o wycenę ale brakuje danych
      if (pricingResult.requiresLeadData && pricingResult.response) {
        setAwaitingLeadData(true);
        
        // Wyświetl prośbę o dane kontaktowe
        const leadRequestMessage: ChatMessage = {
          id: crypto.randomUUID(),
          conversationId: conversation.id,
          role: 'assistant',
          content: pricingResult.response,
          timestamp: new Date(),
          metadata: { requiresLeadData: true }
        };
        
        setMessages(prev => [...prev, leadRequestMessage]);
        await conversationService.saveMessage(leadRequestMessage);
        
        setIsLoading(false);
        return; // Przerwij dalsze przetwarzanie
      }
      
      // Jeśli otrzymaliśmy dane i możemy pokazać wycenę
      if (pricingResult.pricingReady && pricingResult.response) {
        setAwaitingLeadData(false);
        
        // Wyświetl wycenę z podziękowaniem
        const pricingMessage: ChatMessage = {
          id: crypto.randomUUID(),
          conversationId: conversation.id,
          role: 'assistant',
          content: pricingResult.response,
          timestamp: new Date(),
          metadata: { hasPricing: true }
        };
        
        setMessages(prev => [...prev, pricingMessage]);
        await conversationService.saveMessage(pricingMessage);
        
        // Wyczyść sesję wyceny
        pricingWithLeadService.clearSession(conversation.id);
        
        setIsLoading(false);
        return;
      }
      
      // STANDARDOWA LOGIKA: Jeśli to nie jest wycena, kontynuuj normalnie
      
      // Pobierz kontekst konwersacji
      const context = await conversationService.getConversationContext(conversation.id);
      
      // Sprawdź czy to gorący lead (pytanie o cenę, zamówienie, etc.)
      const hotKeywords = ['cen', 'koszt', 'zamów', 'ofert', 'wycen', 'kupi', 'zakup'];
      const isHotLead = hotKeywords.some(keyword => content.toLowerCase().includes(keyword));
      
      if (isHotLead && context.leadInfo) {
        await notificationService.notifyHotLead(context.leadInfo, content);
        console.log('🔥 Powiadomienie o gorącym leadzie wysłane!');
      }
      
      // Sprawdź czy to zapytanie produktowe
      const productKeywords = ['ekspozytor', 'osłon', 'gablon', 'stand', 'pleksi', 'plexi'];
      const isProductInquiry = productKeywords.some(keyword => content.toLowerCase().includes(keyword));
      
      if (isProductInquiry && context.leadInfo) {
        // Analizuj całą rozmowę
        const inquiry = await inquiryService.extractInquiryFromConversation(
          [...messages, userMessage],
          { ...context.leadInfo, conversationId: conversation.id }
        );
        
        if (inquiry) {
          await inquiryService.saveInquiry(inquiry);
          await inquiryService.notifyDetailedInquiry(inquiry, context.leadInfo, messages);
          console.log('📧 Szczegółowe zapytanie przesłane do handlowców!');
        }
      }
      
      // Wyślij do Claude - ale z informacją, że nie może podawać cen bez danych
      const enhancedContext = {
        ...context,
        pricingPolicy: 'NEVER_SHOW_PRICES_WITHOUT_LEAD_DATA',
        awaitingLeadData
      };
      
      const response = await claudeServiceV2.sendMessage(content, enhancedContext);
      
      // Dodaj odpowiedź asystenta
      const assistantMessage: ChatMessage = {
        id: crypto.randomUUID(),
        conversationId: conversation.id,
        role: 'assistant',
        content: response.content,
        timestamp: new Date(),
        metadata: response.metadata
      };
      
      setMessages(prev => [...prev, assistantMessage]);
      await conversationService.saveMessage(assistantMessage);
      
      // Jeśli Claude sugeruje akcję (np. utworzenie oferty)
      if (response.suggestedAction) {
        handleSuggestedAction(response.suggestedAction);
      }
      
    } catch (error) {
      setError('Nie udało się wysłać wiadomości');
      console.error('Error sending message:', error);
      
      // Fallback message
      const errorMessage: ChatMessage = {
        id: crypto.randomUUID(),
        conversationId: conversation.id,
        role: 'assistant',
        content: 'Przepraszam, wystąpił problem. Spróbuj ponownie lub zadzwoń: 884 042 107',
        timestamp: new Date()
      };
      
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const endConversation = async () => {
    if (conversation) {
      await conversationService.endConversation(conversation.id);
      localStorage.removeItem('chatbot_conversation_id');
      pricingWithLeadService.clearSession(conversation.id);
      setConversation(null);
      setMessages([]);
      setAwaitingLeadData(false);
    }
  };

  const handleSuggestedAction = async (action: any) => {
    switch (action.type) {
      case 'create_offer':
        // Przekieruj do formularza oferty z wypełnionymi danymi
        window.location.href = `/offers/new?data=${encodeURIComponent(JSON.stringify(action.data))}`;
        break;
      case 'schedule_meeting':
        // Otwórz kalendarz
        window.open(action.calendarLink, '_blank');
        break;
      case 'show_products':
        // Pokaż produkty w czacie
        // TODO: Implementacja
        break;
    }
  };

  return {
    messages,
    conversation,
    isLoading,
    error,
    awaitingLeadData,
    sendMessage,
    startConversation,
    endConversation
  };
};